<script>
/* eslint-disable prefer-destructuring */
import Vue from "vue";
import DescriptionSection from "WhoDoYouKnowComponents/sections/DescriptionSection.vue";
import ActionSection from "WhoDoYouKnowComponents/sections/ActionSection.vue";
import HeaderSection from "WhoDoYouKnowComponents/sections/HeaderSection.vue";
import FooterSection from "WhoDoYouKnowComponents/sections/FooterSection.vue";
import { mapActions, mapState } from "vuex";
import TrackingMixin from "JS/mixins/campaign/tracking";
import FormValidationMixin from "JS/mixins/campaign/formValidation";
import VeeValidate from "vee-validate";
import AxiosHelper from "JS/helpers/axios";
import AdvocateService from "JS/services/campaign/AdvocateService";
import StakeholderService from "JS/services/campaign/StakeholderService";
import FormHelper from "JS/helpers/FormHelper";
import WillingToCall from "WhoDoYouKnowComponents/WillingToCall.vue";
import StakeholderSearchPopup from "WhoDoYouKnowComponents/StakeholderSearchPopup.vue";
import Routes from "Constants/campaign/whodoyouknow/routeNames";
import { throttle as _throttle } from "lodash";

Vue.use(VeeValidate, { events: "blur", inject: false });

const eventsBus = new Vue({});

export default {
  name: "WhoDoYouKnow",
  components: {
    ActionSection,
    DescriptionSection,
    HeaderSection,
    FooterSection,
    StakeholderSearchPopup,
    WillingToCall
  },
  mixins: [TrackingMixin, FormValidationMixin],
  props: {
    advocateInfo: {
      required: true,
      type: Object
    },
    customerSettings: {
      type: Object,
      required: true
    },
    settings: {
      type: Object,
      required: true
    },
    appSettings: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      innerWidth: 0,
      mobileBreakpoint: 768,
      stakeholderSearchPopupVisible: false,
      trackingPixelImages: [],
      willingToCallVisible: false
    };
  },
  computed: {
    ...mapState("campaign", {
      campaignDesignColors: state => state.campaignDesignColors,
      formFields: state =>
        state.formFields.default ? state.formFields.default.welcome : {},
      pageBackground: state => state.pageBackground,
      unknownErrorMessage: state => state.unknownErrorMessage
    }),
    actionSectionStyles() {
      const actionStyles = {
        color: this.campaignDesignColors.formTextColor
      };
      if (!this.isMobile) {
        actionStyles.background = this.formBackgroundGradient;
        actionStyles.borderColor = this.campaignDesignColors.formBackgroundBottom;
      }
      return actionStyles;
    },
    stakeholderResultsActive() {
      return this.$route.name === Routes.STAKEHOLDER_RESULTS;
    },
    descriptionSectionStyles() {
      const descriptionStyles = {
        desktop: {
          background: this.campaignDesignColors.mainContentBackground,
          color: this.campaignDesignColors.mainContentTextColor
        },
        mobile: {
          color: this.campaignDesignColors.formTextColor
        }
      };
      return !this.isMobile
        ? descriptionStyles.desktop
        : descriptionStyles.mobile;
    },
    findStakeholdersRouteActive() {
      return this.$route.name === Routes.LOOKUP_STAKEHOLDERS;
    },
    formBackgroundGradient() {
      return `linear-gradient(${this.campaignDesignColors.formBackgroundTop}, ${
        this.campaignDesignColors.formBackgroundBottom
      })`;
    },
    isMobile() {
      return this.innerWidth <= this.mobileBreakpoint;
    },
    mobileBodyStyles() {
      if (this.isMobile) {
        return {
          color: this.campaignDesignColors.formTextColor,
          background: this.formBackgroundGradient,
          borderColor: this.campaignDesignColors.formBackgroundBottom
        };
      }
      return {};
    },
    showDescriptionSection() {
      if (this.isMobile) {
        return this.findStakeholdersRouteActive;
      }
      return true;
    },
    showFooterSection() {
      if (this.isMobile && this.thankYouRouteActive) {
        return false;
      }
      return true;
    },
    thankYouRouteActive() {
      return this.$route.name === Routes.THANK_YOU;
    }
  },
  watch: {
    actionCompleted() {
      // return to top of window once an action has been completed
      window.scrollTo({ top: 0 });
      this.trackFormCompleteEvent();

      // watchers in transitions are not firing events
      // emit event for these components
      eventsBus.$emit("actionCompleted");
    }
  },
  beforeMount() {
    // tracking
    eventsBus.$on("handleFormFieldValue", this.trackFormStartEvent);
    eventsBus.$on("actionActivated", () => {
      this.setFormStarted(false);
    });

    eventsBus.$on("findLegislators", payload => {
      if (!payload.background) {
        this.trackFoundLegislatorsEvent();
      }
    });

    this.resetTrackingState();
  },
  created() {
    this.template = Vue.compile(
      window.P2A.campaignSettings.templates[this.$options.name]
    ).render;
  },
  mounted() {
    this.setBackground();
    this.initializeResizing();
    this.initializeHeaderSettings();
    this.initializeFooterSettings();
    this.setInitialOptInStates();

    this.setIsMobile(this.isMobile);
    this.setHoneypotPageLoadTime(this.appSettings.loadStart);
    this.setPrivacyPolicyDisclaimer(
      this.customerSettings.privacyPolicyDisclaimer
    );

    eventsBus.$on(
      "hideStakeholderSearchPopup",
      this.hideStakeholderSearchPopup
    );
    eventsBus.$on(
      "showStakeholderSearchPopup",
      this.showStakeholderSearchPopup
    );
    eventsBus.$on("showWillingToCall", this.showWillingToCall);
    eventsBus.$on("hideWillingToCall", this.hideWillingToCall);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    ...mapActions({
      setHeaderSettings: "wdyk/headerSettings",
      setFooterSettings: "wdyk/footerSettings",
      setFormValue: "forms/formValues",
      setIsMobile: "wdyk/isMobile",
      setHoneypotPageLoadTime: "forms/honeypotPageLoadTime",
      setPrivacyPolicyDisclaimer: "wdyk/privacyPolicyDisclaimer"
    }),
    dispatchModalEvent() {
      const event = document.createEvent("Event");
      event.initEvent("toggleModal", true, true);
      document.dispatchEvent(event);
    },
    emitScrollEvent: _throttle(event => {
      eventsBus.$emit("actionSectionScroll", event);
    }, 500),
    handleResize() {
      this.innerWidth = window.innerWidth;
    },
    initializeHeaderSettings() {
      this.setHeaderSettings({
        campaignLogo: this.settings.campaignLogo,
        campaignLogoAltText: this.settings.campaignLogoAltText
      });
    },
    initializeFooterSettings() {
      this.setFooterSettings({
        content: this.settings.copyright,
        subjectTo: this.settings.subjectTo,
        url: this.customerSettings.privacyPolicyUrl,
        label: this.customerSettings.privacyPolicyUrlLabel,
        andLabel: this.settings.andLabel,
        hidePrivacyPolicy: this.customerSettings.enHidePoliciesFrontend,
        privacyPolicyLabel: this.settings.privacyPolicyLabel,
        privacyPolicyUrl: this.customerSettings.privacyPolicyUrl,
        privacyPolicyUrlLabel: this.customerSettings.privacyPolicyUrlLabel
      });
    },
    initializeResizing() {
      this.handleResize();
      window.addEventListener("resize", this.handleResize);
    },
    setBackground() {
      document.body.style.cssText = this.pageBackground;
    },
    setInitialOptInStates() {
      const subscribeEmail = this.customerSettings.subscriptionDefaultEmail;
      const subscribeSms = this.customerSettings.subscriptionDefaultSms;
      this.setFormValue({ fieldName: "subscribeEmail", value: subscribeEmail });
      this.setFormValue({ fieldName: "subscribeSms", value: subscribeSms });
    },
    hideStakeholderSearchPopup() {
      this.dispatchModalEvent();
      this.stakeholderSearchPopupVisible = false;
    },
    hideWillingToCall() {
      this.dispatchModalEvent();
      this.willingToCallVisible = false;
    },
    showStakeholderSearchPopup() {
      this.dispatchModalEvent();
      this.stakeholderSearchPopupVisible = true;
    },
    showWillingToCall() {
      this.dispatchModalEvent();
      this.willingToCallVisible = true;
    }
  },
  provide() {
    return {
      advocateService: new AdvocateService(this.$store),
      axiosHelper: new AxiosHelper(this.unknownErrorMessage),
      eventsBus,
      formHelper: new FormHelper(this.$store, this.formFields),
      stakeholderService: new StakeholderService(
        this.$store,
        this.$router,
        eventsBus
      )
    };
  },
  render(createElement) {
    if (!this.template) {
      return createElement("div", "Loading...");
    }

    return this.template();
  }
};
</script>
